import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Flame } from "lucide-react";
import { pick } from "../i18n/translations";
import { useApp } from "../context/AppContext";

/**
 * DealOfMomentHero — Hero rouge avec countdown configurable
 *
 * Backend : /api/products?deal=true ne retourne QUE les produits dont :
 *   - badges.deal = true
 *   - ET deal_expires_at non défini OU > now
 *
 * Quand le compte à rebours atteint 0, le composant re-fetch la liste
 * (les produits expirés disparaissent automatiquement via le filtre backend).
 *
 * Le countdown utilise le MIN(deal_expires_at) parmi les produits affichés.
 */
export default function DealOfMomentHero({ compact = false }) {
  const { lang, fmt } = useApp();
  const [deals, setDeals] = useState([]);
  const [now, setNow] = useState(Date.now());

  // Re-fetch when expired
  useEffect(() => {
    fetchDeals();
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  function fetchDeals() {
    api.get("/products?deal=true&limit=8").then((r) => setDeals(r.data || []));
  }

  // Pick earliest expiry among deals
  const expiries = deals.map((p) => p.deal_expires_at).filter(Boolean).map((s) => new Date(s).getTime());
  const earliestExpiry = expiries.length > 0 ? Math.min(...expiries) : null;
  const remaining = earliestExpiry ? earliestExpiry - now : null;

  // Trigger re-fetch when an expiry has passed
  useEffect(() => {
    if (remaining !== null && remaining < 0) {
      fetchDeals();
    }
  }, [remaining !== null && remaining < 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (deals.length === 0) return null;

  const maxVisible = compact ? 8 : 8;
  const visibleDeals = deals.slice(0, maxVisible);
  const visibleCount = visibleDeals.length;

  return (
    <section className={`relative overflow-hidden bg-gradient-to-br from-coral via-coral-dark to-ink text-ivory ${compact ? "" : ""}`} data-testid="deal-hero">
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.3) 0%, transparent 50%)",
      }} />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-12 lg:py-16">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Flame size={18} className="animate-pulse" />
              <span className="text-xs uppercase tracking-[0.3em] font-semibold">{"L'offre du moment"}</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light leading-tight">
              {visibleCount} {visibleCount > 1 ? "produits coup de cœur" : "produit coup de cœur"}
              <br />
              <span className="italic">à prix sage</span>
            </h2>
            <p className="text-ivory/85 text-sm mt-3 max-w-xl">
              Sélection cliniciens — pendant que les stocks durent.
            </p>
          </div>
          {remaining !== null && remaining > 0 && <Countdown ms={remaining} />}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {visibleDeals.map((p) => {
            const linkPath = p.resource_kind === "resource" || p.resource_kind === "pack"
              ? `/ressources-a-telecharger/${p.slug}`
              : `/produit/${p.slug}`;
            return (
            <Link
              key={p.id}
              to={linkPath}
              className="group bg-ivory/10 backdrop-blur-sm hover:bg-ivory/15 transition rounded-2xl p-3 border border-ivory/15"
              data-testid={`deal-${p.slug}`}
            >
              <div className="aspect-square rounded-xl overflow-hidden bg-ivory/5 mb-3">
                {p.images?.[0] && <img src={p.images[0]} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />}
              </div>
              <p className="text-sm font-medium leading-snug line-clamp-2 mb-1">{pick(p.name, lang)}</p>
              <div className="flex items-baseline gap-2">
                {p.sale_price ? (
                  <>
                    <span className="font-display text-lg">{fmt(p.sale_price)}</span>
                    <span className="text-xs line-through opacity-60">{fmt(p.price)}</span>
                  </>
                ) : (
                  <span className="font-display text-lg">{fmt(p.price)}</span>
                )}
                {p.is_digital && <span className="text-[9px] uppercase tracking-widest opacity-70 ms-1">PDF</span>}
              </div>
            </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Countdown({ ms }) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const units = [
    { v: d, label: "jours" },
    { v: h, label: "heures" },
    { v: m, label: "min" },
    { v: s, label: "sec" },
  ];
  return (
    <div className="flex items-center gap-2 bg-ivory/10 backdrop-blur-sm rounded-2xl px-4 py-3 border border-ivory/15" data-testid="deal-countdown">
      <span className="text-[10px] uppercase tracking-widest opacity-80 me-1">⏱️ Se termine dans</span>
      {units.map((u, i) => (
        <div key={i} className="text-center">
          <p className="font-display text-xl leading-none tabular-nums">{String(u.v).padStart(2, "0")}</p>
          <p className="text-[8px] uppercase tracking-widest opacity-70 mt-0.5">{u.label}</p>
        </div>
      ))}
    </div>
  );
}
